import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ListVideo, Play, X } from "lucide-react";
import { Photo } from "./photo";

/**
 * "Videos recomendados": lessons a description points to, shown as cards.
 *
 * The list lives in the lesson description itself (editable from the admin
 * panel), as a section headed "**Videos recomendados:**" — or the earlier
 * "**Videos relacionados:**" — whose bullets link to
 * `…/courses/<slug>?lesson=<id>`, optionally followed by "— why to watch it".
 * An `at=<seconds>` parameter on that link also pops the recommendation up
 * inside the player at that moment of the video, YouTube-card style.
 * The section is lifted out of the Markdown and rendered here instead.
 */
const SECTION =
  /\n*\*\*Videos (?:recomendados|relacionados):\*\*\n((?:[-*] .*(?:\n|$))+)/i;
const LINK =
  /\[([^\]]+)\]\(([^)]*[?&]lesson=([\w-]+)[^)]*)\)(?:\s*[—–-]\s*(.+))?/;
const AT = /[?&]at=(\d+)/;

export type RecommendedLink = {
  id: string;
  label: string;
  reason?: string;
  /** Second of the video at which to pop the card up in the player. */
  at?: number;
};

export function splitRecommendedLessons(markdown: string): {
  body: string;
  links: RecommendedLink[];
} {
  const match = SECTION.exec(markdown);
  if (!match) return { body: markdown, links: [] };
  const links = (match[1] ?? "")
    .split("\n")
    .map((line) => LINK.exec(line))
    .filter((found): found is RegExpExecArray => Boolean(found))
    .map(([, label = "", url = "", id = "", reason]) => {
      // Written after a dash in the list; on its own card it starts a sentence.
      const text = reason?.trim();
      const at = AT.exec(url)?.[1];
      return {
        id,
        label,
        reason: text && text.charAt(0).toUpperCase() + text.slice(1),
        at: at === undefined ? undefined : Number(at),
      };
    });
  return {
    body: (
      markdown.slice(0, match.index) +
      markdown.slice(match.index + match[0].length)
    ).trim(),
    links,
  };
}

export type RecommendedLesson = {
  id: string;
  title: string;
  time: string;
  thumbnailUrl?: string | null;
  module: { number: string; title: string };
  reason?: string;
  at?: number;
};

/** How long a recommendation card stays up in the player, in seconds. */
const CARD_SECONDS = 4;

/**
 * Distance from the video's bottom edge to the card, in px: fixed, so the card
 * doesn't move with the subtitles. Fullscreen gets more room for the larger
 * controls and subtitles.
 */
const CARD_BOTTOM = 75;
const CARD_BOTTOM_FULLSCREEN = 168;

/**
 * In-player recommendation card, bottom right, YouTube-style: one at a time,
 * for CARD_SECONDS from its `at` second. Recommendations sharing a moment
 * follow one another. Mount it keyed by the lesson so a dismissal only lasts
 * for that lesson.
 */
export function RecommendedVideoCards({
  lessons,
  position,
  onOpen,
}: {
  lessons: RecommendedLesson[];
  position: number;
  onOpen: (id: string) => void;
}) {
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const timed = lessons.filter((lesson) => lesson.at !== undefined);
  const firstAt = Math.min(...timed.map((lesson) => lesson.at ?? Infinity));
  // The top-right list button shows up once the first card has popped up,
  // and stays even if the student seeks back before it. Adjusting state
  // while rendering is React's documented pattern for this kind of latch.
  const [revealed, setRevealed] = useState(false);
  if (!revealed && position >= firstAt) setRevealed(true);
  const current = lessons
    .filter((lesson) => lesson.at !== undefined)
    .find((lesson, index, timed) => {
      // Cards sharing a moment queue up instead of stacking over the video.
      const queued = timed
        .slice(0, index)
        .filter((other) => other.at === lesson.at).length;
      const start = (lesson.at ?? 0) + queued * CARD_SECONDS;
      return (
        position >= start &&
        position < start + CARD_SECONDS &&
        !dismissed.includes(lesson.id)
      );
    });
  const visible = current ? [current] : [];
  // Whether the video shell is fullscreen (the player is told to take the
  // shell fullscreen, so this card goes along with it).
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const update = () => {
      const shell = document.getElementById("lesson-video-shell");
      setFullscreen(
        Boolean(shell && document.fullscreenElement?.contains(shell)),
      );
    };
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  const open = (id: string) => {
    setListOpen(false);
    onOpen(id);
  };
  return (
    <>
      {revealed && (
        <div className="absolute right-3 top-3 z-10 flex flex-col items-end sm:right-4 sm:top-4">
          <button
            type="button"
            onClick={() => setListOpen((value) => !value)}
            aria-expanded={listOpen}
            aria-label="Videos recomendados"
            title="Videos recomendados"
            className={`border-ink bg-paper border-chip grid h-9 w-9 cursor-pointer place-items-center rounded-full transition-opacity duration-200 hover:opacity-100 focus-visible:opacity-100 ${listOpen ? "opacity-100" : "opacity-50"}`}
          >
            <ListVideo size={17} />
          </button>
          <AnimatePresence>
            {listOpen && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="border-ink bg-paper shadow-brutal border-chip mt-2 w-[min(17rem,calc(100vw-3rem))]"
              >
                <p className="text-ink-soft border-ink/15 border-b px-3 py-2 font-mono text-[0.65rem] font-bold uppercase tracking-widest">
                  Videos recomendados
                </p>
                <ul className="max-h-60 overflow-y-auto">
                  {lessons.map((lesson) => (
                    <li key={lesson.id}>
                      <button
                        type="button"
                        onClick={() => open(lesson.id)}
                        className="hover:bg-ink/5 group flex w-full cursor-pointer items-center gap-2.5 p-2 text-left"
                      >
                        <span className="border-ink bg-ink relative aspect-video w-16 shrink-0 overflow-hidden border">
                          {lesson.thumbnailUrl && (
                            <Photo
                              src={lesson.thumbnailUrl}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          )}
                        </span>
                        <span className="font-display line-clamp-2 text-xs font-extrabold leading-tight group-hover:underline">
                          {lesson.title}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      <div
        className="pointer-events-none absolute right-2 z-10 flex w-[min(15rem,62%)] flex-col items-end sm:right-4 sm:w-[min(17rem,45%)]"
        style={{
          bottom: fullscreen ? CARD_BOTTOM_FULLSCREEN : CARD_BOTTOM,
        }}
      >
        <AnimatePresence>
          {visible.map((lesson) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 24 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="border-ink bg-paper shadow-brutal border-chip pointer-events-auto absolute bottom-0 right-0 flex w-full items-stretch"
            >
              <button
                type="button"
                onClick={() => open(lesson.id)}
                className="group flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 p-1.5 pr-8 text-left sm:p-2 sm:pr-8"
              >
                <span className="border-ink bg-ink relative hidden aspect-video w-20 shrink-0 overflow-hidden border sm:block">
                  {lesson.thumbnailUrl && (
                    <Photo
                      src={lesson.thumbnailUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="text-ink-soft flex items-center gap-1 font-mono text-[0.6rem] font-bold uppercase tracking-widest">
                    <Play size={10} fill="currentColor" aria-hidden="true" />
                    Video recomendado
                  </span>
                  <span className="font-display line-clamp-2 block text-xs font-extrabold leading-tight group-hover:underline sm:text-sm">
                    {lesson.title}
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setDismissed((current) => [...current, lesson.id])
                }
                aria-label="Ocultar recomendación"
                className="hover:bg-ink/10 absolute right-0.5 top-0.5 grid h-7 w-7 cursor-pointer place-items-center"
              >
                <X size={13} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
}

export function RecommendedLessons({
  lessons,
  onOpen,
}: {
  lessons: RecommendedLesson[];
  onOpen: (id: string) => void;
}) {
  if (!lessons.length) return null;
  return (
    <section className="mt-7" aria-labelledby="recommended-lessons-title">
      <h3
        id="recommended-lessons-title"
        className="font-display text-lg font-extrabold"
      >
        Videos recomendados
      </h3>
      <ul className="mt-4 grid gap-4 sm:grid-cols-2">
        {lessons.map((lesson) => (
          <li key={lesson.id}>
            <button
              type="button"
              onClick={() => onOpen(lesson.id)}
              className="border-ink bg-paper shadow-brutal border-chip group flex h-full w-full cursor-pointer flex-col text-left transition-[transform,box-shadow] duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5 focus-visible:-translate-x-0.5 focus-visible:-translate-y-0.5"
            >
              <span className="border-ink bg-ink border-b-(length:--border-chip) relative block aspect-video w-full overflow-hidden">
                {lesson.thumbnailUrl && (
                  <Photo
                    src={lesson.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                )}
                {/* Same badge as the other lesson thumbnails: small,
                    translucent, at the title card's height. */}
                <span className="border-ink bg-bubble border-chip absolute left-1/2 top-[54%] grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full opacity-60 transition-[transform,opacity] duration-200 group-hover:scale-110 group-hover:opacity-100">
                  <Play size={16} fill="currentColor" className="ml-0.5" />
                </span>
              </span>
              <span className="flex flex-1 flex-col gap-1 p-4">
                <span className="text-ink-soft font-mono text-xs uppercase tracking-widest">
                  Módulo {lesson.module.number} · {lesson.time}
                </span>
                <span className="font-display text-base font-extrabold leading-tight">
                  {lesson.title}
                </span>
                {lesson.reason && (
                  <span className="text-ink-soft text-sm leading-snug">
                    {lesson.reason}
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
